type EnvSource = Record<string, string | boolean | number | undefined> | undefined;

export const RUNTIME_CONFIG_GLOBAL = '__APP_RUNTIME_CONFIG__' as const;

declare global {
  interface Window {
    [RUNTIME_CONFIG_GLOBAL]?: EnvSource;
  }
}

const getRuntimeEnv = (): EnvSource => (
  typeof window !== 'undefined' ? window[RUNTIME_CONFIG_GLOBAL] : undefined
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getViteEnv = (): EnvSource => (typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined);

const getNodeEnv = (): EnvSource => (typeof process !== 'undefined' ? process.env : undefined);

const getEnvVarFromSources = (
  keys: string[] | string,
  sources: EnvSource[],
  fallback?: string,
): string | undefined => {
  const keyList = Array.isArray(keys) ? keys : [keys];

  for (const key of keyList) {
    for (const source of sources) {
      if (source && source[key] !== undefined) {
        return String(source[key]);
      }
    }
  }

  return fallback;
};

export const getEnvVar = (keys: string[] | string, fallback?: string): string | undefined =>
  getEnvVarFromSources(keys, [getRuntimeEnv(), getViteEnv(), getNodeEnv()], fallback);

/**
 * Debug logging utility - only logs when VITE_DEBUG=true
 * Usage: import { debug } from '../utils/env'; debug.log('message', data);
 */
const isDebugMode = (): boolean => {
  const sources: EnvSource[] = [getRuntimeEnv(), getViteEnv(), getNodeEnv()];
  for (const source of sources) {
    if (source) {
      const val = source['VITE_DEBUG'] || source['DEBUG'];
      if (val === true || val === 'true' || val === '1') return true;
    }
  }
  return false;
};

export const debug = {
  log: (...args: unknown[]): void => {
    if (isDebugMode()) console.log(...args);
  },
  warn: (...args: unknown[]): void => {
    if (isDebugMode()) console.warn(...args);
  },
  error: (...args: unknown[]): void => {
    // Always log errors, even in production
    console.error(...args);
  },
};

const parseEnvFlag = (value: string | undefined, fallback: boolean): boolean => {
  if (value === undefined) {
    return fallback;
  }

  switch (String(value).toLowerCase()) {
    case 'true':
    case '1':
    case 'yes':
    case 'on':
      return true;
    case 'false':
    case '0':
    case 'no':
    case 'off':
      return false;
    default:
      return fallback;
  }
};

export const getEnvFlag = (keys: string[] | string, fallback = false): boolean =>
  parseEnvFlag(getEnvVar(keys), fallback);

// Security-sensitive flags such as the authentication dev-mode bypass must be
// sealed at build time; diagnostic flags intentionally continue using getEnvFlag.
export const getBuildEnvFlag = (keys: string[] | string, fallback = false): boolean =>
  parseEnvFlag(getEnvVarFromSources(keys, [getViteEnv(), getNodeEnv()]), fallback);

export const getEnvInt = (keys: string[] | string, fallback: number): number => {
  const value = getEnvVar(keys);
  if (value === undefined) {
    return fallback;
  }

  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};
